import { atom, getDefaultStore } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { graphql } from '~/gql'
import { useInvalidateCommonQuery } from '~/hooks/useCommonQuery'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { useNotify } from '~/hooks/useNotify'
import { useInvalidateSurveyForSettingsQuery } from '~/hooks/useSurveyForSettingsQuery'
import { type BrandGroup } from '~/hooks/useSurveyForSettingsQuery'
import { applyIfExist, compact, exists, findByPath, getByPath } from '~/utils'

const document = graphql(`
  query BrandGroupListPage_GetMappingResults($awaitIds: [String!]!) {
    getMappingResults(awaitIds: $awaitIds) {
      arrived
      result {
        brandGroupId
        success
      }
      error
    }
  }
`)

type Item = {
  awaitId: string
  brandGroup: BrandGroup
  onMappingComplete?: () => void
}

const itemsAtom = atom<Item[]>([])
const timeoutIdAtom = atom<number | undefined>(undefined)

const refetchInterval = 5000

/**
 * マッピング中のブランドグループを監視し、マッピング処理が完了したら通知を表示するカスタムフックです。
 *
 * @returns - 監視するブランドグループを追加する関数。
 */
export const useAddMappingObservation = () => {
  const graphQLRequest = useGraphQLRequest()
  const notify = useNotify()
  const invalidateCommonQuery = useInvalidateCommonQuery()
  const invalidateSurveyForSettingsQuery = useInvalidateSurveyForSettingsQuery()
  const { t } = useTranslation()

  const refetchUntilAllArrived = React.useCallback(async () => {
    const store = getDefaultStore()
    const items = store.get(itemsAtom)

    const awaitIds = items.map(R.prop('awaitId'))
    const result = await graphQLRequest({ document, variables: { awaitIds } })
    const arrivedResults = R.pipe(
      result.getMappingResults ?? [],
      R.filter(R.prop('arrived')),
    )

    if (arrivedResults.length === 0) {
      const timeoutId = window.setTimeout(
        refetchUntilAllArrived,
        refetchInterval,
      )
      store.set(timeoutIdAtom, timeoutId)

      return
    }

    for (const arrivedResult of arrivedResults) {
      const name = applyIfExist(
        arrivedResult.result?.brandGroupId,
        (brandGroupId) =>
          R.pipe(
            items,
            findByPath('brandGroup.id', brandGroupId),
            getByPath('brandGroup.name'),
          ),
      )

      if (arrivedResult.result?.success ?? false) {
        notify({
          content: exists(name)
            ? t('name_mapping_process_completed', { name })
            : t('mapping_process_completed'),
          style: 'success',
        })
      } else {
        const error = arrivedResult.error
        notify({
          content: compact([
            exists(name)
              ? t('name_mapping_process_failed', { name })
              : t('mapping_process_failed'),
            error,
          ]).join('\n'),
          style: 'error',
        })
      }

      const item = items.find(
        (item) => item.brandGroup.id === arrivedResult.result?.brandGroupId,
      )
      item?.onMappingComplete?.()
    }

    invalidateCommonQuery()
    invalidateSurveyForSettingsQuery()

    const newItems = R.differenceWith(
      items,
      arrivedResults,
      ({ brandGroup }, arrivedResult) =>
        arrivedResult.result?.brandGroupId === brandGroup.id,
    )
    store.set(itemsAtom, newItems)
    if (0 < newItems.length) {
      const timeoutId = window.setTimeout(
        refetchUntilAllArrived,
        refetchInterval,
      )
      store.set(timeoutIdAtom, timeoutId)
    } else {
      store.set(timeoutIdAtom, undefined)
    }
  }, [
    graphQLRequest,
    invalidateCommonQuery,
    invalidateSurveyForSettingsQuery,
    notify,
    t,
  ])

  type Params = {
    awaitId: string
    brandGroup: BrandGroup
    onMappingComplete?: () => void
  }

  return React.useCallback(
    ({ awaitId, brandGroup, onMappingComplete }: Params) => {
      const store = getDefaultStore()
      store.set(itemsAtom, (items) => [
        ...items,
        { awaitId, brandGroup, onMappingComplete },
      ])

      const timeoutId = store.get(timeoutIdAtom)
      if (!exists(timeoutId)) {
        const newTimeoutId = window.setTimeout(
          refetchUntilAllArrived,
          refetchInterval,
        )
        store.set(timeoutIdAtom, newTimeoutId)
      }
    },
    [refetchUntilAllArrived],
  )
}
